import React, { useState, useRef, useEffect, useContext } from 'react';
import { ChatContext } from '../contexts/ChatContext';
import ChartRenderer from '../ChartRenderer';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { Send } from 'react-bootstrap-icons';
import TextoConFotos from './TextoConFotos';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    fontFamily: 'Arial, sans-serif'
  },
  header: {
    padding: '10px 16px',
    borderBottom: '1px solid #eee',
    fontWeight: 'bold'
  },
  messagesArea: {
    flex: 1,
    minHeight: 0,
    padding: '16px',
    overflowY: 'auto',
    backgroundColor: '#f9f9f9'
  },
  inputArea: {
    padding: '10px',
    borderTop: '1px solid #eee',
    display: 'flex'
  },
  input: {
    flex: 1,
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    marginRight: '5px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    resize: 'none',
    minHeight: '38px',
    maxHeight: '120px',
    overflowY: 'auto'
  },
  msgUser: {
    backgroundColor: '#e3f2fd',
    padding: '8px',
    borderRadius: '10px 10px 0 10px',
    marginBottom: '8px',
    alignSelf: 'flex-end',
    maxWidth: '80%',
    marginLeft: 'auto'
  },
  msgBot: {
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    padding: '8px',
    borderRadius: '10px 10px 10px 0',
    marginBottom: '8px',
    maxWidth: '95%'
  },
  typingDots: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 0'
  },
  typingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#2563eb',
    animation: 'chatPulse 1.4s infinite ease-in-out both'
  }
};

// Misma lógica de exportación que ChatWidget.jsx (widget flotante) — se mantiene
// duplicada a propósito para no arriesgar el comportamiento del widget ya probado.
function cleanDataForExport(data) {
  if (!Array.isArray(data)) return data;
  return data.map(row => {
    const cleanRow = {};
    for (const [key, value] of Object.entries(row)) {
      if (key === '_id') continue;
      if (value === null || value === undefined || value === '') continue;
      if (Array.isArray(value) && value.length === 0) continue;
      if (typeof value === 'object' && !Array.isArray(value)) {
        const keys = Object.keys(value);
        if (keys.includes('_bsontype') || keys.includes('$oid')) continue;
      }
      cleanRow[key] = value;
    }
    return cleanRow;
  });
}

function downloadExcel(data, filename = 'datos') {
  try {
    let worksheetData = Array.isArray(data) ? data : (data && typeof data === 'object' ? [data] : []);
    const cleanedData = cleanDataForExport(worksheetData);
    if (!cleanedData || cleanedData.length === 0) {
      alert('No hay datos disponibles para exportar');
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(cleanedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `${filename}_${timestamp}.xlsx`);
  } catch (error) {
    console.error('Error generando Excel:', error);
    alert('Error al generar el archivo Excel');
  }
}

async function downloadPDF(messageContent, chartData = null, chartElementId = null, filename = 'reporte') {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let yPosition = 20;

    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(`Reporte ${import.meta.env.VITE_NOMBRE_INSTITUCION || 'Asistente IA'}`, 14, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Fecha: ${new Date().toLocaleString()}`, 14, yPosition);
    yPosition += 10;

    if (messageContent) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Respuesta:', 14, yPosition);
      yPosition += 8;

      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      const splitText = doc.splitTextToSize(messageContent, pageWidth - 28);
      doc.text(splitText, 14, yPosition);
      yPosition += (splitText.length * 5) + 10;
    }

    if (chartElementId) {
      const chartElement = document.getElementById(chartElementId);
      if (chartElement) {
        try {
          const canvas = await html2canvas(chartElement, { backgroundColor: '#ffffff', scale: 2 });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = pageWidth - 28;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          if (yPosition + imgHeight > doc.internal.pageSize.height - 20) {
            doc.addPage();
            yPosition = 20;
          }
          doc.addImage(imgData, 'PNG', 14, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10;
        } catch (error) {
          console.warn('No se pudo capturar la gráfica:', error);
        }
      }
    }

    if (!chartElementId && chartData && chartData.data && Array.isArray(chartData.data) && chartData.data.length > 0) {
      const cleanedData = cleanDataForExport(chartData.data.slice(0, 50));
      if (cleanedData && cleanedData.length > 0) {
        const headers = Object.keys(cleanedData[0]);
        const rows = cleanedData.map(row => headers.map(header => row[header] || ''));
        doc.autoTable({
          head: [headers],
          body: rows,
          startY: yPosition,
          margin: { left: 14, right: 14 },
          styles: { fontSize: 8 },
          headStyles: { fillColor: [0, 123, 255] }
        });
      }
    }

    const timestamp = new Date().toISOString().split('T')[0];
    doc.save(`${filename}_${timestamp}.pdf`);
  } catch (error) {
    console.error('Error generando PDF:', error);
    alert('Error al generar el archivo PDF');
  }
}

/**
 * UI de mensajes + input, compartida con el widget flotante vía el mismo ChatContext.
 * Pensada para ocupar el 100% de alto del contenedor donde se monte (página dedicada).
 */
export default function ChatPanel() {
  const { messages, isLoading, loadingConv, enviar } = useContext(ChatContext);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isLoading && inputRef.current) inputRef.current.focus();
  }, [isLoading]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const currentInput = input;
    setInput('');
    await enviar(currentInput);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>Asistente {import.meta.env.VITE_NOMBRE_INSTITUCION}</div>

      <div style={styles.messagesArea}>
        {loadingConv && <p style={{ textAlign: 'center', color: '#888', fontSize: '13px' }}>Cargando conversación…</p>}
        {!loadingConv && messages.length === 0 && (
          <div style={styles.msgBot}>¡Hola! Soy tu asistente de datos. Pregúntame sobre ventas, clientes, inventario o proveedores. También puedo generar gráficas. ¡Intenta!</div>
        )}
        {messages.map((msg, index) => (
          <div key={index}>
            <div style={msg.role === 'user' ? styles.msgUser : styles.msgBot}>
              <TextoConFotos content={msg.text} esUsuario={msg.role === 'user'} />
              {msg.chart && (
                <div id={`chart-page-${index}`}>
                  <ChartRenderer config={msg.chart} enableClick={true} />
                </div>
              )}
            </div>

            {msg.role === 'assistant' && (
              (msg.chart && msg.chart.data && msg.chart.data.length > 0) ||
              (msg.rawData && msg.rawData.length > 0)
            ) && (
              <div style={{ marginTop: '5px', marginBottom: '10px', display: 'flex', gap: '8px', paddingLeft: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={async () => {
                    const dataToExport = msg.chart || { data: msg.rawData };
                    const chartId = msg.chart ? `chart-page-${index}` : null;
                    await downloadPDF(msg.text, dataToExport, chartId, 'reporte');
                  }}
                  style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer' }}
                >
                  📄 PDF
                </button>
                <button
                  onClick={() => {
                    const dataToExport = (msg.chart && msg.chart.data) || msg.rawData;
                    downloadExcel(dataToExport, 'datos');
                  }}
                  style={{ backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer' }}
                >
                  📊 Excel
                </button>
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div style={{ ...styles.msgBot, fontStyle: 'italic', color: '#666' }}>
            <span style={styles.typingDots}>
              <span style={{ ...styles.typingDot, animationDelay: '-0.32s' }} />
              <span style={{ ...styles.typingDot, animationDelay: '-0.16s' }} />
              <span style={styles.typingDot} />
            </span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form style={styles.inputArea} onSubmit={handleSend}>
        <textarea
          ref={inputRef}
          style={styles.input}
          value={input}
          onChange={handleInputChange}
          placeholder="Escribe tu pregunta..."
          disabled={isLoading}
          rows={1}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend(e);
            }
          }}
        />
        <button type="submit" disabled={isLoading} style={{ cursor: 'pointer', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', padding: '8px 15px' }}>
          {isLoading ? '...' : <Send size={16} />}
        </button>
      </form>
    </div>
  );
}
